import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Package,
  ClipboardList,
  ShoppingBag,
  Receipt,
  Store,
  Wallet,
  Users as UsersIcon,
  Building2,
  CheckSquare,
  LogOut,
  ScrollText,
  PackageSearch,
  Settings2,
  Clock,
  Database,
  MapPin,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";

// Helper to format IST time
const formatISTTime = () => {
  const now = new Date();
  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000;
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
  const istTime = new Date(utcTime + istOffset);
  
  let hours = istTime.getHours();
  const minutes = String(istTime.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${hours}:${minutes} ${ampm}`;
};

const baseItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", key: "dashboard" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory", key: "inventory" },
  { to: "/stock", label: "Stock", icon: PackageSearch, testid: "nav-stock", countKey: "expiring_soon", key: "stock" },
  { to: "/requirement", label: "Requirements", icon: ClipboardList, testid: "nav-requirement", countKey: "requirements", key: "requirement" },
  { to: "/purchase", label: "Purchase orders", icon: ShoppingBag, testid: "nav-purchase", countKey: "purchase_orders", key: "purchase" },
  { to: "/orders", label: "Orders received", icon: Receipt, testid: "nav-orders", countKey: "orders_received", key: "orders" },
  { to: "/sales", label: "Sales", icon: Store, testid: "nav-sales", key: "sales" },
  { to: "/customers", label: "Customers", icon: UsersIcon, testid: "nav-customers", key: "customers" },
  { to: "/suppliers", label: "Suppliers", icon: Building2, testid: "nav-suppliers", key: "suppliers" },
  { to: "/payment-sources", label: "Payment sources", icon: Wallet, testid: "nav-payment-sources", key: "payment_sources" },
  { to: "/todo", label: "To-Do", icon: CheckSquare, testid: "nav-todo", countKey: "todo", key: "todo" },
];

export const ALL_SECTION_KEYS = [
  "dashboard", "inventory", "stock", "requirement", "purchase", "orders",
  "sales", "customers", "suppliers", "payment_sources", "todo",
  "users", "activity", "shop_settings", "places",
];

export const SECTION_LABELS = {
  dashboard: "Dashboard",
  inventory: "Inventory",
  stock: "Stock",
  requirement: "Requirements",
  purchase: "Purchase orders",
  orders: "Orders received",
  sales: "Sales",
  customers: "Customers",
  suppliers: "Suppliers",
  payment_sources: "Payment sources",
  todo: "To-Do",
  users: "Users",
  activity: "Activity",
  shop_settings: "Shop Settings",
  places: "Places",
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({});
  const [istTime, setIstTime] = useState(formatISTTime());

  // Update IST clock every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setIstTime(formatISTTime());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);

  const rawItems = user?.role === "master"
    ? [
        ...baseItems,
        { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users", key: "users" },
        { to: "/activity", label: "Activity", icon: ScrollText, testid: "nav-activity", key: "activity" },
        { to: "/places", label: "Places", icon: MapPin, testid: "nav-places", key: "places" },
        { to: "/shop-settings", label: "Shop Settings", icon: Settings2, testid: "nav-shop-settings", key: "shop_settings" },
        { to: "/product-data-review", label: "Data Review", icon: AlertTriangle, testid: "nav-data-review", key: "data_review" },
        { to: "/backup", label: "Backup", icon: Database, testid: "nav-backup", key: "backup" },
      ]
    : user?.role === "super_user"
    ? [
        ...baseItems,
        { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users", key: "users" },
        { to: "/activity", label: "Activity", icon: ScrollText, testid: "nav-activity", key: "activity" },
        { to: "/places", label: "Places", icon: MapPin, testid: "nav-places", key: "places" },
        { to: "/shop-settings", label: "Shop Settings", icon: Settings2, testid: "nav-shop-settings", key: "shop_settings" },
        { to: "/product-data-review", label: "Data Review", icon: AlertTriangle, testid: "nav-data-review", key: "data_review" },
      ]
    : [
        ...baseItems,
        { to: "/places", label: "Places", icon: MapPin, testid: "nav-places", key: "places" },
      ];

  // Masters and super_users always see every section. Staff/super_user see only their `allowed_sections`.
  // If allowed_sections is empty, they see NO menu items (must be granted access by admin).
  const items = (() => {
    if (user?.role === "master" || user?.role === "super_user") return rawItems;
    const allowed = Array.isArray(user?.allowed_sections) ? user.allowed_sections : [];
    if (allowed.length === 0) return []; // No access granted yet
    return rawItems.filter((it) => allowed.includes(it.key));
  })();

  const fetchCounts = async () => {
    try {
      const { data } = await api.get("/dashboard/sidebar-counts");
      setCounts(data || {});
    } catch (_e) {
      /* silent */
    }
  };

  useEffect(() => {
    fetchCounts();
    const id = setInterval(() => {
      fetchCounts();
    }, 60000);
    const onRefresh = () => fetchCounts();
    window.addEventListener("dyynit:sidebar-refresh", onRefresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("dyynit:sidebar-refresh", onRefresh);
    };
  }, []);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="h-screen overflow-hidden grain-bg">
      {/* Fixed Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 z-40 w-64 h-screen flex-col border-r border-[hsl(var(--border))] bg-white/95 backdrop-blur-sm overflow-y-auto">
        {/* Logo + IST Clock */}
        <div className="px-4 py-3 border-b border-[hsl(var(--border))] sticky top-0 bg-white/95 backdrop-blur-sm z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 shrink-0 rounded-md overflow-hidden flex items-center justify-center bg-white border border-[hsl(var(--border))]">
              <img src={BRAND.logoUrl} alt={BRAND.name} className="w-full h-full object-contain" />
            </div>
            <div className="font-bold text-sm tracking-[0.25em] leading-tight">{BRAND.short}</div>
            {/* IST Clock */}
            <div className="ml-auto shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">
              <Clock className="w-3 h-3 text-slate-500" />
              <span className="text-[10px] font-mono font-medium text-slate-700 whitespace-nowrap" data-testid="ist-clock">
                {istTime}
              </span>
            </div>
          </div>
        </div>

        {/* User Info & Logout */}
        <div className="px-4 py-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground truncate">
                {user?.name || "User"}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground truncate">
                  {user?.phone || ""}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                  user?.role === "master" 
                    ? "bg-[#9E6D5B] text-white" 
                    : "bg-[hsl(var(--muted))] text-muted-foreground"
                }`}>
                  {user?.role === "master" ? "MASTER" : user?.role || "staff"}
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={onLogout}
              className="shrink-0 h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {items.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-8 px-4">
              <div className="text-base font-medium mb-2">No Access</div>
              <div className="text-xs">Contact your administrator to get menu access.</div>
            </div>
          )}
          {items.map((it) => {
            const count = it.countKey ? Number(counts[it.countKey] || 0) : 0;
            // Special handling for todo: show both assigned to me and created by me counts
            const isTodo = it.countKey === "todo";
            const todoCreatedCount = isTodo ? Number(counts.todo_created || 0) : 0;
            const showTodoBadge = isTodo && (count > 0 || todoCreatedCount > 0);
            // Special handling for expiring_soon: use warning color
            const isExpiringSoon = it.countKey === "expiring_soon";
            const showRegularBadge = !isTodo && !isExpiringSoon && it.countKey && count > 0;
            const showExpiringSoonBadge = isExpiringSoon && count > 0;
            
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
                data-testid={it.testid}
              >
                <it.icon className="w-4 h-4" strokeWidth={1.8} />
                <span className="flex-1">{it.label}</span>
                {showRegularBadge && (
                  <span
                    className="ml-auto text-[10px] font-mono-data font-semibold px-1.5 py-0.5 rounded-full bg-[#9E6D5B]/10 text-[#9E6D5B] min-w-[20px] text-center"
                    data-testid={`nav-count-${it.countKey}`}
                  >
                    {count}
                  </span>
                )}
                {showExpiringSoonBadge && (
                  <span
                    className="ml-auto text-[10px] font-mono-data font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 min-w-[20px] text-center"
                    data-testid="nav-count-expiring-soon"
                    title="Items expiring within 30 days"
                  >
                    {count}
                  </span>
                )}
                {showTodoBadge && (
                  <span className="ml-auto flex items-center gap-1" data-testid="nav-count-todo">
                    {count > 0 && (
                      <span
                        className="text-[10px] font-mono-data font-semibold px-1.5 py-0.5 rounded-full bg-[#9E6D5B]/10 text-[#9E6D5B] min-w-[20px] text-center"
                        title="Assigned to me"
                      >
                        {count}
                      </span>
                    )}
                    {todoCreatedCount > 0 && (
                      <span
                        className="text-[10px] font-mono-data font-semibold px-1.5 py-0.5 rounded-full bg-[#6B8E9E]/10 text-[#6B8E9E] min-w-[20px] text-center"
                        title="Created by me"
                      >
                        {todoCreatedCount}
                      </span>
                    )}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Main Content - Scrollable */}
      <main className="md:ml-64 h-screen overflow-y-auto">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-sm border-b border-[hsl(var(--border))] sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md overflow-hidden bg-white border border-[hsl(var(--border))] flex items-center justify-center">
              <img src={BRAND.logoUrl} alt={BRAND.name} className="w-full h-full object-contain" />
            </div>
            <span className="font-heading text-sm">{BRAND.short}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `p-2 rounded-md ${isActive ? "bg-foreground text-white" : "text-muted-foreground"}`
                }
                data-testid={`m-${it.testid}`}
                title={it.label}
              >
                <it.icon className="w-4 h-4" />
              </NavLink>
            ))}
            <Button size="sm" variant="ghost" onClick={onLogout} data-testid="logout-btn-mobile">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
